import React, { memo, useCallback, useMemo } from 'react';
import CouponSection from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/CouponSection';
import CouponSection3 from '@wconcept/wconcept-ui-renewal/src/components/organisms/_section/CouponSection3';
import CouponItem from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/CouponItem';
import CouponItem3 from '@wconcept/wconcept-ui-renewal/src/components/molecules/_item/CouponItem3';
import {
  ModuleDisplayType,
  PromotionCouponModule,
  PromotionNotice,
} from '@wconcept/wconcept-common/models/renewal/promotion';
import {
  ICoupon,
  ICouponInfo,
} from '@wconcept/wconcept-common/models/event/custom';
import { setComma } from '@wconcept/wconcept-common/utils/commonUtils';
import { useCouponDownload } from '@hooks/useCouponDownload';
import useGa4Event from '@hooks/useGa4Event';
import numberToKorean from '@wconcept/wconcept-common/utils/numberToKorean';
import dayjs from '@wconcept/wconcept-common/libs/dayjs';
import { type PromotionModuleVisualTokens } from '@utils/promotionContentVisual';
import { usePromotionModuleCustomColors } from '@hooks/promotion/usePromotionModuleCustomColors';
import { isInDisplayTimeRange } from '../utils/displayTimeChecker';

/** 쿠폰팩형 최대 노출 그룹 수 */
const MAX_PACK_GROUP_COUNT = 6;

/** 쿠폰팩형 그룹(팩)당 최대 쿠폰 수 (어드민 등록 상한과 동일한 방어값) */
const MAX_COUPONS_PER_PACK = 20;

const resolveModuleBgColor = (
  colorType: string | { bgColor: string }
): string => {
  if (typeof colorType === 'object') return colorType.bgColor;
  return colorType === 'black' ? '#000' : '#fff';
};

interface CouponModuleProps {
  bgType?: string;
  visualTokens: PromotionModuleVisualTokens;
  title?: string;
  subTitle?: string;
  couponData?: PromotionCouponModule[];
  displayType?: ModuleDisplayType;
  noticeData?: PromotionNotice[];
  order: number;
  contentId?: string;
  isDesktop?: boolean;
  registerModule?: (contentId: string, element: HTMLElement | null) => void;
}

function CouponModule({
  bgType,
  visualTokens,
  title,
  subTitle,
  couponData,
  noticeData,
  displayType,
  order,
  contentId,
  isDesktop,
  registerModule,
}: CouponModuleProps) {
  const { downloadMultipleCoupons, downloadSingleCoupon, isLoading } =
    useCouponDownload();
  const { sendGa4CouponClick, sendGa4ButtonClick } = useGa4Event();

  // 쿠폰 필터링
  const getCoupontFilter = useCallback(
    (v: ICoupon[]) =>
      v.filter(
        item =>
          !item.soldOut &&
          item.ableDown &&
          isInDisplayTimeRange(item.issueStartDate, item.issueEndDate)
      ),
    []
  );

  const isCouponPack = displayType === 'PACK';

  const packGroups = useMemo(() => {
    if (!isCouponPack || !couponData) return [];
    return couponData
      .map(group => ({
        group,
        availableCoupons: getCoupontFilter(group.couponItems).slice(
          0,
          MAX_COUPONS_PER_PACK
        ),
      }))
      .filter(({ availableCoupons }) => availableCoupons.length > 0)
      .sort((a, b) => a.group.displayOrder - b.group.displayOrder)
      .slice(0, MAX_PACK_GROUP_COUNT);
  }, [isCouponPack, couponData, getCoupontFilter]);

  // 모든 쿠폰 다운로드
  const onClickCouponDownloadAll = useCallback(async () => {
    const targetCouponData = isCouponPack
      ? packGroups.map(({ availableCoupons }) => availableCoupons)
      : couponData?.map(v => getCoupontFilter(v.couponItems).slice(0, 8));

    const targetCouponCds = targetCouponData?.flatMap(v =>
      v.map(item => item.couponCd)
    );
    const targetCouponNames = targetCouponData?.flatMap(v =>
      v.map(item => item.couponName)
    );

    sendGa4CouponClick({
      area: 'promotion_coupon_download_all',
      listNo: order,
      text: '한 번에 받기',
    });
    await downloadMultipleCoupons(targetCouponCds || []);
  }, [
    isCouponPack,
    packGroups,
    couponData,
    sendGa4CouponClick,
    order,
    downloadMultipleCoupons,
    getCoupontFilter,
  ]);

  // 개별 쿠폰 다운로드
  const onClickItem = useCallback(
    async (coupon: ICoupon, index: number, subTitleText: string) => {
      sendGa4CouponClick({
        area: 'promotion_coupon_download',
        couponCd: coupon.couponCd,
        couponNm: coupon.couponName,
        listNo: index + 1,
        text: subTitleText,
      });
      await downloadSingleCoupon(coupon.couponCd);
    },
    [downloadSingleCoupon, sendGa4CouponClick]
  );

  const getDesc = useCallback((v: ICouponInfo) => {
    if (v.maxDiscountAmount) {
      return `최대 ${numberToKorean(v.maxDiscountAmount, 'mixed')}원 할인`;
    }
    return '';
  }, []);
  // 쿠폰 아이템 렌더
  const getCouponItems = useCallback(
    (
      couponList: ICoupon[],
      couponMainColor: string,
      couponTextColor: 'WHITE' | 'BLACK',
      subTitleText: string
    ) => {
      if (couponList.length > 0) {
        return couponList.map((v, index) => (
          <CouponItem
            key={v.couponCd}
            title={v.couponName}
            desc={getDesc(v)}
            type={displayType?.toLowerCase()}
            mainColor={couponMainColor}
            textColor={couponTextColor?.toLowerCase()}
            isDesktop={isDesktop}
            onClick={() => {
              if (displayType === 'CARD') {
                onClickItem(v, index, subTitleText);
              }
            }}
          />
        ));
      }
      return null;
    },
    [displayType, onClickItem, getDesc, isDesktop]
  );

  const getCouponGroups = useCallback(
    () =>
      couponData?.map(v => ({
        content: getCouponItems(
          getCoupontFilter(v.couponItems).slice(0, 8),
          v.couponColor,
          v.couponTextColor,
          v.subTitle
        ),
        bgColor: v.backgroundColor,
        titleColor: v.subTextColor,
        groupTitle: v.subTitle,
      })) || [],
    [couponData, getCouponItems, getCoupontFilter]
  );

  const onClickNoticeToggle = useCallback(
    (active: boolean) => {
      sendGa4ButtonClick({
        area: 'promotion_coupon_notice_button',
        listNo: order,
        text: `유의사항 ${active ? '보기' : '닫기'}`,
      });
    },
    [sendGa4ButtonClick, order]
  );

  // \n을 <br>로 변환하고 <br>을 배열로 반환
  const getNoticeList = useCallback(
    () =>
      noticeData
        ?.map(v => v.text.replace(/\n/g, '<br>').split('<br>'))
        ?.flat() || [],
    [noticeData]
  );

  const isCouponDataEmpty =
    couponData?.flatMap(group => group.couponItems).length === 0;

  const { presetBgType, customColorsLite } = usePromotionModuleCustomColors({
    bgType,
    visualTokens,
    mask: 'twoField',
  });

  const sectionColorType = customColorsLite ?? presetBgType;

  if (isCouponDataEmpty) {
    return null;
  }

  // 쿠폰팩형: 노출 가능 그룹이 1개 미만이면 모듈 전체 비노출
  if (isCouponPack && packGroups.length === 0) {
    return null;
  }

  // 쿠폰팩형 렌더
  if (isCouponPack) {
    return (
      <CouponSection3
        title={title}
        subTitle={subTitle}
        colorType={sectionColorType}
        isDesktop={isDesktop}
        isLoaded
        couponList={packGroups.map(({ group, availableCoupons }) => (
          <CouponItem3
            key={group.moduleId}
            title={group.title}
            desc={group.benefitText}
            colorType={{
              pointColor: group.couponColor,
              emphasisTextColor:
                group.couponTextColor === 'WHITE' ? '#fff' : '#000',
              bgColor: resolveModuleBgColor(sectionColorType),
            }}
          />
        ))}
        noticeList={
          noticeData && noticeData.length > 0 ? getNoticeList() : undefined
        }
        onClick={onClickCouponDownloadAll}
        onNoticeToggle={onClickNoticeToggle}
      />
    );
  }

  return (
    <CouponSection
      title={title}
      subTitle={subTitle}
      type={displayType?.toLowerCase()}
      groups={getCouponGroups()}
      noticeList={
        noticeData && noticeData.length > 0 ? getNoticeList() : undefined
      }
      colorType={sectionColorType}
      onClick={onClickCouponDownloadAll}
      onNoticeToggle={onClickNoticeToggle}
    />
  );
}

export default memo(CouponModule);
